import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import { Link, useNavigate } from "react-router-dom";
import { config } from "../../config.js";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import "./Drivers.css";
import { toast } from "react-toastify";
import { FaRegStar, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { LuArrowLeft } from "react-icons/lu";

// ========================
// Main Component
// ========================
function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [searchPin, setSearchPin] = useState("");
  const [loading, setLoading] = useState(true);
  const { isSidebar, adminPermissions } = useContext(SidebarContext);
  const role = localStorage.getItem("adminRole");
  const [previewImage, setPreviewImage] = useState(null);
  const [pincodeModal, setPincodeModal] = useState(null);
  const navigate = useNavigate();
  const openImageModal = (img) => {
    setPreviewImage(img);
  };

  const closeImageModal = () => {
    setPreviewImage(null);
  };
  const adminCity =
    role === "sub-admin" ? localStorage.getItem("adminCity") : null;
  const StarRating = ({ rating = 0 }) => {
    const totalStars = 5;

    const safeRating = Math.max(0, Math.min(5, Number(rating)));
    const fullStars = Math.floor(safeRating);
    const hasHalfStar = safeRating % 1 >= 0.5;
    const emptyStars = totalStars - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <span className="d-flex align-items-center gap-1">
        <span className="d-flex align-items-center gap-1">
          {/* Full Stars */}
          {[...Array(fullStars)].map((_, i) => (
            <FaStar key={`full-${i}`} style={{ color: "#ffc107" }} />
          ))}

          {/* Half Star */}
          {hasHalfStar && (
            <FaStarHalfAlt key="half" style={{ color: "#ffc107" }} />
          )}

          {/* Empty Stars */}
          {[...Array(emptyStars)].map((_, i) => (
            <FaRegStar key={`empty-${i}`} style={{ color: "#6c757d" }} />
          ))}
        </span>
      </span>
    );
  };
  const getDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config?.backEndBaseUrl}driver/get${adminCity ? `?adc=${adminCity.toLocaleLowerCase()}` : ""}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result.status && Array.isArray(result.drivers)) {
        setDrivers(result.drivers);
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Fetching drivers failed:", error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);
  const filteredDrivers = useMemo(() => {
    if (!searchPin.trim()) return drivers;

    return drivers.filter((driver) =>
      (driver.pincodes || []).some((pin) =>
        pin.toString().includes(searchPin.trim()),
      ),
    );
  }, [drivers, searchPin]);
  useEffect(() => {
    getDrivers();
  }, [getDrivers]);

  // Update locally (no full refetch)
  const handleUpdate = (uid, newStatus, key) => {
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.uid === uid ? { ...driver, [key]: newStatus } : driver,
      ),
    );
  };

  const ToggleButtonApprove = ({ uid, initialStatus, onUpdate }) => {
    const [status, setStatus] = useState(initialStatus);
    const role = localStorage.getItem("adminRole"); // make sure role is available
    const adminPermissions = JSON.parse(
      localStorage.getItem("adminPermissions"),
    ); // or pass as prop/context

    const handleToggleApprove = async () => {
      if (adminPermissions?.driver_edit || role === "admin") {
        const newStatus = !status;
        setStatus(newStatus);
        try {
          const response = await fetch(
            `${config.backEndBaseUrl}driver/status/approve/${uid}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
            },
          );

          if (!response.ok) throw new Error("Network response was not ok");
          if (onUpdate) onUpdate(uid, newStatus);
        } catch (error) {
          console.error("Error updating approval status:", error);
          setStatus(!newStatus);
        }
      } else {
        toast.error("You don't have permission to change approval status.");
      }
    };

    return (
      <div className="toggle-container" onClick={handleToggleApprove}>
        <div className={`toggle-switch ${status ? "active" : ""}`}>
          <div className="toggle-knob"></div>
        </div>
        <span className={`toggle-text ${status ? "active" : ""}`}>
          {status ? "active" : "deactive"}
        </span>
      </div>
    );
  };

  const ToggleButtonStatus = ({ uid, initialStatus, onUpdate }) => {
    const [status, setStatus] = useState(initialStatus);
    const { adminPermissions } = useContext(SidebarContext);
    const role = localStorage.getItem("adminRole"); // or pass via context

    const handleToggleStatus = async () => {
      if (adminPermissions?.driver_edit || role === "admin") {
        const newStatus = !status;
        setStatus(newStatus);
        try {
          const response = await fetch(
            `${config.backEndBaseUrl}driver/block/${uid}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
            },
          );

          if (!response.ok) throw new Error("Network response was not ok");
          if (onUpdate) onUpdate(uid, newStatus);
        } catch (error) {
          console.error("Error updating active status:", error);
          setStatus(!newStatus);
        }
      } else {
        toast.error("You don't have permission to change active status.");
      }
    };

    return (
      <div className="toggle-container" onClick={handleToggleStatus}>
        <div className={`toggle-switch ${status ? "active" : ""}`}>
          <div className="toggle-knob"></div>
        </div>
        <span className={`toggle-text ${status ? "active" : ""}`}>
          {status ? "active" : "deactive"}
        </span>
      </div>
    );
  };
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "userName",
        Cell: ({ row }) => {
          const user = row.original;
          const img = user.photo || "/adminAssets/images/user_1.avif";

          return (
            <div className="user-cell">
              <img
                src={img}
                alt={user.userName}
                className="user-avatar"
                onClick={() => openImageModal(img)}
              />

              <div className="user-info">
                <div className="user-name">{user.userName}</div>
              </div>
            </div>
          );
        },
      },
      { accessorKey: "phoneNumber", header: "Phone" },
      {
        accessorKey: "idProof.frontImage",
        header: "ID Proof Front",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const img =
            row.original.idProof?.frontImage || config.placeHolderImage;

          return (
            <div
              className="doc-cell"
              onClick={() => openImageModal(img)}
              title="View ID Proof"
            >
              <img
                src={img}
                alt="ID Front"
                className="doc-image doc-image-blur"
              />
              <span className="doc-plus">+</span>
            </div>
          );
        },
      },
      {
        accessorKey: "licence.frontImage",
        header: "Licence Front",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const img =
            row.original.licence?.frontImage || config.placeHolderImage;

          return (
            <div
              className="doc-cell"
              onClick={() => openImageModal(img)}
              title="View Licence"
            >
              <img
                src={img}
                alt="Licence Front"
                className="doc-image doc-image-blur"
              />
              <span className="doc-plus">+</span>
            </div>
          );
        },
      },
      {
        accessorKey: "bank.passbookImage",
        header: "Bank Passbook",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const img =
            row.original.bank?.passbookImage || config.placeHolderImage;

          return (
            <div
              className="doc-cell"
              onClick={() => openImageModal(img)}
              title="View Passbook"
            >
              <img
                src={img}
                alt="Passbook"
                className="doc-image doc-image-blur"
              />
              <span className="doc-plus">+</span>
            </div>
          );
        },
      },
      {
        accessorKey: "walletBalance",
        header: "Wallet Balance",
        Cell: ({ cell }) => {
          const walletBalance = cell.row.original.walletBalance;

          const amount = Number(walletBalance);

          return (
            <span>{Number.isFinite(amount) ? amount.toFixed(2) : "N/A"}</span>
          );
        },
      },
      {
        id: "walletLedger",
        header: "Wallet",
        Cell: ({ row }) => (
          <Link
            className="btn btn-sm btn-outline-primary d-flex align-items-center"
            style={{ width: "80px" }}
            to={`/driver/wallet/${row.original.uid}`}
          >
            View ledger
          </Link>
        ),
      },
      { accessorKey: "dob", header: "DOB" },
      {
        accessorKey: "serviceCities",
        header: "City",
        Cell: ({ cell }) => {
          const serviceCities = cell.row.original.serviceCities || [];
          if (!serviceCities.length) return "-";

          return (
            <div>
              {serviceCities.map((u, i) => (
                <div key={i}>{u}</div>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "pincodes",
        header: "Pincodes",
        Cell: ({ cell }) => {
          const pincodes = cell.row.original.pincodes || [];

          if (!pincodes.length) return "-";

          return (
            <div className="pincode-cell">
              <div className="pincode-chips">
                {pincodes.slice(0, 1).map((pin) => (
                  <span className="pincode-chip" key={pin}>
                    {pin}
                  </span>
                ))}
              </div>
              {pincodes.length > 1 && (
                <button
                  className="pincode-more"
                  type="button"
                  onClick={() =>
                    setPincodeModal({
                      name: cell.row.original.userName,
                      pincodes,
                    })
                  }
                >
                  +{pincodes.length - 1} more
                </button>
              )}
            </div>
          );
        },
      },
      { accessorKey: "gender", header: "Gender" },
      {
        accessorKey: "vehicle.carType",
        header: "Car Type",
        Cell: ({ row }) => {
          const carTypes = row.original.vehicle?.carType || [];

          if (!carTypes.length) return "-";

          return (
            <div>
              {carTypes.map((type, i) => (
                <div key={i}>{type}</div>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "vehicle.gearType",
        header: "Gear Type",
        Cell: ({ row }) => {
          const gearTypes = row.original.vehicle?.gearType || [];

          if (!gearTypes.length) return "-";

          return (
            <div>
              {gearTypes.map((type, i) => (
                <div key={i}>{type}</div>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "userRating",
        header: "Rating",
        Cell: ({ row }) => {
          const rating = row.original.userRating;

          const rate = rating?.rate;
          const count = rating?.count ?? 0;

          return (
            <div>
              <small className="text-muted">
                <StarRating rating={rate || 0} />
                {Number.isFinite(rate)
                  ? (rate / count).toFixed(1)
                  : "No rating"}{" "}
                | {count} reviews
              </small>
            </div>
          );
        },
      },
      {
        accessorKey: "isActive",
        header: "Active",
        Cell: ({ cell }) => (
          <ToggleButtonStatus
            uid={cell.row.original.uid}
            initialStatus={cell.getValue()}
            onUpdate={(uid, newStatus) =>
              handleUpdate(uid, newStatus, "isActive")
            }
          />
        ),
      },
      {
        accessorKey: "approved",
        header: "Approved",
        Cell: ({ cell }) => (
          <ToggleButtonApprove
            uid={cell.row.original.uid}
            initialStatus={cell.getValue()}
            onUpdate={(uid, newStatus) =>
              handleUpdate(uid, newStatus, "approved")
            }
          />
        ),
      },
      {
        accessorKey: "uid",
        header: "Update",
        Cell: ({ cell }) => {
          return role === "admin" || adminPermissions?.driver_edit ? (
            <Link
              to={`/driver/update/${cell.row.original.uid}`}
              className="btn text-light btn-success d-flex align-items-center justify-content-center"
              style={{ width: "80px" }}
            >
              Update
            </Link>
          ) : (
            <span className="text-muted">No Access</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : "0px" }}
              >
                <div className="main-content-inner">
                  <div className="main-content-wrap">
                    <div className="tf-section">
                      <div className="wg-box" style={{ marginBottom: 20 }}>
                        <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <button
                              className="back-btn flex items-center gap-2"
                              onClick={() => navigate("/")}
                            >
                              <LuArrowLeft size={16} />
                              {/* <span>Back</span> */}
                            </button>

                            <h5 className="fare-heading">Drivers Overview</h5>
                          </div>
                          <div className="driver-search">
                            <input
                              type="text"
                              placeholder="Search by pincode..."
                              value={searchPin}
                              onChange={(e) => setSearchPin(e.target.value)}
                              className="driver-search-input"
                            />
                          </div>
                        </div>

                        {loading ? (
                          <div className="skeleton-table">
                            {[...Array(6)].map((_, i) => (
                              <div className="skeleton-row" key={i}>
                                <div className="skeleton skeleton-name"></div>
                                <div className="skeleton skeleton-photo"></div>
                                <div className="skeleton skeleton-phone"></div>
                                <div className="skeleton skeleton-email"></div>
                                <div className="skeleton skeleton-status"></div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            <div className="driver-table">
                              <ReactTable
                                columns={columns}
                                data={filteredDrivers}
                              />
                            </div>
                            {previewImage && (
                              <div
                                className="image-modal-overlay"
                                onClick={closeImageModal}
                              >
                                <div className="image-modal-content">
                                  <img src={previewImage} alt="Preview" />
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {pincodeModal && (
                          <div
                            className="pincode-modal-overlay"
                            onClick={() => setPincodeModal(null)}
                          >
                            <div
                              className="pincode-modal"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <div className="pincode-modal-header">
                                <div>
                                  <h3>{pincodeModal.name || "Driver"}</h3>
                                  <p>DRIVER'S AVAILABLE PINCODES</p>
                                  
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setPincodeModal(null)}
                                >
                                  ×
                                </button>
                              </div>
                              <div className="pincode-modal-list">
                                {pincodeModal.pincodes.map((pin) => (
                                  <span className="pincode-chip" key={pin}>
                                    {pin}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Drivers;
